import * as React from "react"
import { Command as CommandPrimitive } from "cmdk"
import { Search } from "lucide-react"

import { cn } from "@/lib/utils"
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import * as VisuallyHidden from "@radix-ui/react-visually-hidden"

const Command = React.forwardRef(({ className, ...props }, ref) => (
  <CommandPrimitive
    ref={ref}
    className={cn(
      "flex h-full w-full flex-col overflow-hidden rounded-[22px] bg-popover/98 text-popover-foreground",
      className
    )}
    {...props} />
))
Command.displayName = CommandPrimitive.displayName

const CommandDialog = ({
  children,
  title = "Command Palette",
  description = "Buscar, navegar e executar ações rapidamente",
  shouldFilter,
  filter,
  ...props
}) => {
  return (
    <Dialog {...props}>
      <DialogContent
        className="
          w-[calc(100vw-32px)]
          max-w-2xl
          overflow-hidden
          rounded-[24px]
          border
          border-border/70
          bg-popover/95
          p-0
          shadow-[0_24px_70px_rgba(15,23,42,0.14)]
          backdrop-blur-xl
        "
      >
        <VisuallyHidden.Root>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </VisuallyHidden.Root>
        <Command
          shouldFilter={shouldFilter}
          filter={filter}
          className="
            [&_[cmdk-group-heading]]:px-3
            [&_[cmdk-group-heading]]:font-semibold
            [&_[cmdk-group-heading]]:uppercase
            [&_[cmdk-group-heading]]:tracking-[0.12em]
            [&_[cmdk-group-heading]]:text-muted-foreground
            [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-1
            [&_[cmdk-group]]:px-2
            [&_[cmdk-input-wrapper]_svg]:h-4
            [&_[cmdk-input-wrapper]_svg]:w-4
            [&_[cmdk-input-wrapper]_svg]:text-primary
            [&_[cmdk-input]]:h-14
            [&_[cmdk-item]]:px-3
            [&_[cmdk-item]]:py-3
            [&_[cmdk-item]_svg]:h-4
            [&_[cmdk-item]_svg]:w-4
          ">
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
}

const CommandInput = React.forwardRef(({ className, ...props }, ref) => (
  <div
    className="mx-3 mt-3 flex items-center rounded-xl border border-border/70 bg-muted/25 px-3 transition-colors focus-within:border-primary/30 focus-within:bg-background"
    cmdk-input-wrapper=""
  >
    <Search className="mr-2.5 h-4 w-4 shrink-0 text-primary/80" />
    <CommandPrimitive.Input
      ref={ref}
      className={cn(
        "flex h-12 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground/80 disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      {...props} />
  </div>
))

CommandInput.displayName = CommandPrimitive.Input.displayName

const CommandList = React.forwardRef(({ className, ...props }, ref) => (
  <CommandPrimitive.List
    ref={ref}
    className={cn("max-h-[420px] overflow-y-auto overflow-x-hidden px-1 pb-3 pt-2", className)}
    {...props} />
))

CommandList.displayName = CommandPrimitive.List.displayName

const CommandEmpty = React.forwardRef((props, ref) => (
  <CommandPrimitive.Empty
    ref={ref}
    className="py-10 text-center text-sm text-muted-foreground"
    {...props}
  />
))

CommandEmpty.displayName = CommandPrimitive.Empty.displayName

const CommandGroup = React.forwardRef(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={cn(
      "overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-2 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:text-[9px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.14em] [&_[cmdk-group-heading]]:text-muted-foreground/80",
      className
    )}
    {...props} />
))

CommandGroup.displayName = CommandPrimitive.Group.displayName

const CommandSeparator = React.forwardRef(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator ref={ref} className={cn("mx-2 my-1 h-px bg-border/60", className)} {...props} />
))
CommandSeparator.displayName = CommandPrimitive.Separator.displayName

const CommandItem = React.forwardRef(({ className, ...props }, ref) => (
  <CommandPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex cursor-default select-none items-center gap-2.5 rounded-xl border border-transparent px-3 py-2.5 text-sm outline-none transition-all data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 data-[selected=true]:border-primary/10 data-[selected=true]:bg-primary/[0.07] data-[selected=true]:text-primary data-[selected=true]:shadow-[0_5px_16px_rgba(124,58,237,0.06)] [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-muted-foreground data-[selected=true]:[&_svg]:text-primary",
      className
    )}
    {...props} />
))

CommandItem.displayName = CommandPrimitive.Item.displayName

const CommandShortcut = ({
  className,
  ...props
}) => {
  return (
    <span
      className={cn("ml-auto text-xs tracking-widest text-muted-foreground", className)}
      {...props} />
  );
}
CommandShortcut.displayName = "CommandShortcut"

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
}
